import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertCircle } from "lucide-react";
import { useRejectExpense } from "../hooks/useExpenseActions";
import type { PendingExpense } from "../hooks/usePendingExpenses";
import {
  formatExpenseCategory,
  formatCurrency,
  parseExpenseDate,
} from "../../utils/expense-schema";
import { format } from "date-fns";

const rejectExpenseSchema = z.object({
  rejectionNote: z.string().min(1, "Rejection note is required"),
});

type RejectExpenseInput = z.infer<typeof rejectExpenseSchema>;

interface ExpenseRejectDialogProps {
  expense: PendingExpense | null;
  organizationBusinessUnitId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExpenseRejectDialog({
  expense,
  organizationBusinessUnitId,
  open,
  onOpenChange,
}: ExpenseRejectDialogProps) {
  const { mutateAsync: rejectExpense, isPending } = useRejectExpense();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RejectExpenseInput>({
    resolver: zodResolver(rejectExpenseSchema),
  });

  const onSubmit = async (data: RejectExpenseInput) => {
    if (!expense) {
      return;
    }

    try {
      await rejectExpense({
        organizationBusinessUnitId,
        expenseId: expense.expenseId,
        rejectionNote: data.rejectionNote,
      });

      // Close dialog and reset form
      reset();
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
    }
    onOpenChange(newOpen);
  };

  if (!expense) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg dialog-safe-height overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reject Expense</DialogTitle>
          <DialogDescription>
            Please provide a reason for rejecting this expense. The provider
            will be able to see this note.
          </DialogDescription>
        </DialogHeader>

        {/* Expense details */}
        <div className="p-4 bg-slate-50 rounded-md border space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">Provider</div>
            <div className="text-sm font-medium text-slate-900">
              {expense.providerFirstName} {expense.providerLastName}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">Category</div>
            <div className="text-sm font-medium text-slate-900">
              {formatExpenseCategory(expense.category)}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">Amount</div>
            <div className="text-sm font-medium text-slate-900">
              {formatCurrency(expense.amount)}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">Date</div>
            <div className="text-sm font-medium text-slate-900">
              {format(parseExpenseDate(expense.expenseDate), "MMM d, yyyy")}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Rejection Note */}
          <div className="space-y-2">
            <Label htmlFor="rejectionNote">
              Rejection Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="rejectionNote"
              placeholder="Please explain why this expense is being rejected..."
              rows={4}
              {...register("rejectionNote")}
            />
            {errors.rejectionNote && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.rejectionNote.message}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject Expense"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
